import { attr, customElement, FASTElement, observable } from '@microsoft/fast-element';
import { DrilldownImpliedCarbonGraphStyles as styles } from './drilldown-implied-carbon-graph.styles';
import {
  DrilldownImpliedCarbonGraphTemplate as template,
  OUTCOMES_ENUM,
  GRAPH_SCENARIO,
} from './drilldown-implied-carbon-graph.template';
import Highcharts, { Chart } from 'highcharts';
import { getDatesFromData, getValuesFromData } from '../../../../../utils/chart-data-utils';
import { AGGREGATION_TYPE, GRAPH_PERIODICITY, logger } from '../../../../../utils';
import { IProjectForecastTs, VppaRankings } from '../../../../../types';
import { IVppaService } from '../../../../../services';
import { notifyError } from '../../../../../utils/notify';
import { getFormattedNumber } from "../../../../../utils/formatting";

const name = 'drilldown-implied-carbon-graph';

@customElement({
  name,
  template,
  styles,
})
export class DrilldownImpliedCarbonGraph extends FASTElement {
  @IVppaService vppaService: IVppaService;
  @observable forecastDataArray: Array<any> = [];
  @observable forecastData: Array<IProjectForecastTs>;
  @observable impliedCarbonPriceChart!: HTMLElement;
  @observable periodicity: GRAPH_PERIODICITY = GRAPH_PERIODICITY.QUATERLY;
  @observable scenario: GRAPH_SCENARIO = GRAPH_SCENARIO.BASELINE;
  @observable outcome: OUTCOMES_ENUM = OUTCOMES_ENUM.MEAN;
  @observable disabledOptionsButton: boolean = true;
  @observable projectNames: Array<String> = [];
  @observable chart: Chart;
  @observable colors = {};

  connectedCallback() {
    super.connectedCallback();
  }

  public renderGraph(forecastDataArray, projectNames) {
    this.forecastDataArray = forecastDataArray;
    this.projectNames = projectNames;
    if (this.forecastDataArray.length) {
      this.disabledOptionsButton = false;
      this.renderImpliedCarbonPrice();
    } else {
      this.renderDisabledGraph();
    }
  }

  private getDataArrays(projectNames): Array<any> {
    const dataArrays: any[] = [];
    if (this.forecastDataArray.length) {
      this.forecastDataArray.forEach((forecastData, index) => {
        switch (this.outcome) {
          case OUTCOMES_ENUM.P95:
            dataArrays.push({
              type: 'line',
              color: forecastData.length ? this.colors[forecastData[0]?.VPPA_ID] : '',
              name: projectNames[index],
              showInLegend: false,
              data: getValuesFromData(
                forecastData,
                this.getP95Value(this.scenario),
                this.periodicity,
                this.periodicity !== GRAPH_PERIODICITY.MONTHLY ? AGGREGATION_TYPE.AVG : null
              ),
            });
            break;
          case OUTCOMES_ENUM.MEAN:
            dataArrays.push({
              type: 'line',
              color: forecastData.length ? this.colors[forecastData[0].VPPA_ID] : '',
              name: projectNames[index],
              showInLegend: false,
              data: getValuesFromData(
                forecastData,
                this.getMeanValue(this.scenario),
                this.periodicity,
                this.periodicity !== GRAPH_PERIODICITY.MONTHLY ? AGGREGATION_TYPE.AVG : null
              ),
            });
            break;
          case OUTCOMES_ENUM.P05:
            dataArrays.push({
              type: 'line',
              color: forecastData.length ? this.colors[forecastData[0]?.VPPA_ID] : '',
              name: projectNames[index],
              showInLegend: false,
              data: getValuesFromData(
                forecastData,
                this.getP05Value(this.scenario),
                this.periodicity,
                this.periodicity !== GRAPH_PERIODICITY.MONTHLY ? AGGREGATION_TYPE.AVG : null
              ),
            });
            break;
          default:
            break;
        }
      });
    }

    return dataArrays;
  }

  public renderImpliedCarbonPrice() {

    const dates =
      this.forecastDataArray.length && this.forecastDataArray[0].length
        ? getDatesFromData(this.forecastDataArray[0], this.periodicity)
        : [];
    const dataArrays = this.getDataArrays(this.projectNames);

    this.chart = Highcharts.chart({
      chart: {
        renderTo: this.impliedCarbonPriceChart,
        animation: false,
      },
      title: {
        text: '',
      },
      xAxis: {
        categories: dates,
      },
      boost: {
          seriesThreshold: 5,
          usePreallocated: true,
          useGPUTranslations: true,
      },
      yAxis: [
        {
          // Y-Axis
          title: {
            text: '$/MT CO2',
          },
        },
      ],
      tooltip: {
        enabled: true,
        formatter: function () {
          let tooltip = this.x.toString();
          this.points?.forEach((point) => {
            const colorMarker = `<span style="color: ${point.color};">&#9679;</span>`;
            tooltip +=
              '<br>' +
              colorMarker +
              ` ${point.series.name} : ${
                getFormattedNumber(point.y, '', 2, '$')
              }`;
          });
          return tooltip;
        },
      },
      series: dataArrays,
      plotOptions: {
        series: {
          animation: false,
        },
      },
    });

    this.reflow();
  }

  public reflow() {
    if (this.chart) {
      this.chart.reflow();
    }
  }

  public scenarioChanged(oldValue, newValue) {
    if (this.forecastDataArray && this.forecastDataArray.length && oldValue !== newValue) {
      this.renderImpliedCarbonPrice();
    }
  }

  public outcomeChanged(oldValue, newValue) {
    if (this.forecastDataArray && this.forecastDataArray.length && oldValue !== newValue) {
      this.renderImpliedCarbonPrice();
    }
  }

  public renderDisabledGraph() {
    this.forecastData = [];
    this.forecastDataArray = [];
    this.disabledOptionsButton = true;
    this.renderImpliedCarbonPrice();
  }

  private getP05Value(scenario): string {
    switch (scenario) {
      case GRAPH_SCENARIO.BASELINE:
        return 'SETTLEMENT_ICP_BASELINE_P_05_USD_PER_MT_CO_2E';
      case GRAPH_SCENARIO.HIGH_GAS:
        return 'SETTLEMENT_ICP_HIGH_GAS_P_05_USD_PER_MT_CO_2E';
      case GRAPH_SCENARIO.LOW_GAS:
        return 'SETTLEMENT_ICP_LOW_GAS_P_05_USD_PER_MT_CO_2E';
      default:
        return null;
    }
  }

  private getMeanValue(scenario): string {
    switch (scenario) {
      case GRAPH_SCENARIO.BASELINE:
        return 'SETTLEMENT_ICP_BASELINE_MEAN_USD_PER_MT_CO_2E';
      case GRAPH_SCENARIO.HIGH_GAS:
        return 'SETTLEMENT_ICP_HIGH_GAS_MEAN_USD_PER_MT_CO_2E';
      case GRAPH_SCENARIO.LOW_GAS:
        return 'SETTLEMENT_ICP_LOW_GAS_MEAN_USD_PER_MT_CO_2E';
      default:
        return null;
    }
  }

  private getP95Value(scenario): string {
    switch (scenario) {
      case GRAPH_SCENARIO.BASELINE:
        return 'SETTLEMENT_ICP_BASELINE_P_95_USD_PER_MT_CO_2E';
      case GRAPH_SCENARIO.HIGH_GAS:
        return 'SETTLEMENT_ICP_HIGH_GAS_P_95_USD_PER_MT_CO_2E';
      case GRAPH_SCENARIO.LOW_GAS:
        return 'SETTLEMENT_ICP_LOW_GAS_P_95_USD_PER_MT_CO_2E';
      default:
        return null;
    }
  }
}
